'use client';

import React, { ReactNode, useState } from 'react';
import { Code2Icon } from 'lucide-react';
import { KeyboardShortcutToken } from '~/components/keyboard-shortcut-token';
import { Button } from '~/components/ui/Button';
import { ScrollArea, ScrollBar } from '~/components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '~/components/ui/sheet';
import { Model } from '~/services/models.types';
import { ApiExample } from './api-example';

export type ApiCodeSamplesConfig = {
  model: Model;
  body: Record<string, any>;
  headers?: Record<string, any>;
  path?: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  overides?: Array<{
    language: 'typescript' | 'curl' | 'python' | 'java' | 'go' | 'shell';
    value: string;
  }>;
};

export type ApiCodeSamples = {
  typescript: string;
  python: string;
  java?: string;
  go?: string;
  shell: string;
};

export type ApiExample = {
  name: string;
  description: string | ReactNode;
  samples: ApiCodeSamples;
};

export function ApiButton({ examples, referenceDocs }: { examples: ApiExample[]; referenceDocs?: string | ReactNode }) {
  const [sheetOpen, setSheetOpen] = useState(false);

  return (
    <Sheet
      open={sheetOpen}
      onOpenChange={() => {
        setSheetOpen(!sheetOpen);
      }}
    >
      <SheetTrigger asChild>
        <Button variant="outline" size="xs">
          <Code2Icon className="h-4 w-4" />
          <span className="ml-2 hidden max-md:block max-sm:hidden">Code</span>
          <span className="ml-2 max-md:hidden">View Code</span>

          <KeyboardShortcutToken
            keys="a"
            className="hidden"
            onShortcut={() => {
              setSheetOpen(!sheetOpen);
            }}
          />
        </Button>
      </SheetTrigger>
      <SheetContent className="z-[100] m-0 w-[650px] max-w-[100vw] p-0 max-sm:w-screen sm:min-w-[740px]">
        <SheetHeader className="mx-4 my-2">
          <SheetTitle>Code Examples</SheetTitle>
          {/* <SheetDescription>Make changes to your profile here. Click save when you're done.</SheetDescription> */}
        </SheetHeader>
        <ScrollArea className="h-screen px-4 py-2">
          {examples.map((example) => (
            <div key={example.name} className="mb-10 grid">
              <h2 className="text-base font-bold">{example.name}</h2>
              <p className="text-sm font-normal text-zinc-700 dark:text-zinc-500">{example.description}</p>
              <ApiExample name={example.name} samples={example.samples} />
            </div>
          ))}
          {referenceDocs}
          <ScrollBar orientation="vertical" />
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
